import React, { ReactNode, useState, useRef, useEffect } from "react";
import "./MultiSelect.scss";
interface MultiSelectOption {
  label: string;
  value: string;
  /** Shown before the label - a colour swatch, an icon. */
  icon?: ReactNode;
}
interface MultiSelectProps {
  label: string;
  options: MultiSelectOption[];
  selected: string[] | string;
  onChange: (value: string[] | string) => void;
  multiple?: boolean;
  formatSelected?: (value: string[] | string) => string;
  allButton?: boolean;
  presetOptions?: { label: string; values: string[] }[];
  /** Offer a search box above the list. For pickers with many options. */
  searchable?: boolean;
}
export function MultiSelect<T extends string>({
  label,
  options,
  selected,
  onChange,
  multiple = false,
  formatSelected,
  allButton = false,
  presetOptions = [],
  searchable = false,
}: MultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  // Only the options are searched. "Alla" and the presets stay put, and still
  // act on every option rather than on what the search happens to show.
  const visibleOptions =
    searchable && query.trim()
      ? options.filter((o) =>
          o.label.toLowerCase().includes(query.trim().toLowerCase()),
        )
      : options;

  const isSelected = (val: string) =>
    multiple ? (selected as string[]).includes(val) : selected === val;

  const toggleValue = (val: string) => {
    if (!multiple) {
      onChange(val);
      setOpen(false);
      return;
    }
    const current = selected as string[];
    onChange(
      current.includes(val)
        ? current.filter((v) => v !== val)
        : [...current, val],
    );
  };

  useEffect(() => {
    const closeOnClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", closeOnClickOutside);
    return () => document.removeEventListener("mousedown", closeOnClickOutside);
  }, []);

  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  return (
    <div
      className="multiselect-container"
      ref={ref}
      onClick={() => setOpen((o) => !o)}
    >
      <div className="multiselect text-m">
        {formatSelected ? formatSelected(selected) : label}
      </div>

      {open && (
        <div
          className="multiselect-options-wrapper"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="multiselect-options">
            {searchable && (
              <input
                className="multiselect-search text-m"
                value={query}
                autoFocus
                placeholder="Sök"
                onChange={(e) => setQuery(e.target.value)}
              />
            )}
            <div className="multiselect-option-list">
              {allButton && (
                <div className="multiselect-option text-m">
                  <span className="option-label">Alla</span>
                  <label className="toggle text-m">
                    <input
                      type="checkbox"
                      checked={(selected as string[]).length === options.length}
                      onChange={() => {
                        const allValues = options.map((o) => o.value);
                        const currentlyAllSelected =
                          (selected as string[]).length === allValues.length;
                        onChange(currentlyAllSelected ? [] : allValues);
                      }}
                    />
                    <span className="slider round"></span>
                  </label>
                </div>
              )}
              {presetOptions.map((preset) => {
                const allSelected =
                  Array.isArray(selected) &&
                  preset.values.every((v) =>
                    (selected as string[]).includes(v),
                  );

                return (
                  <div key={preset.label} className="multiselect-option text-m">
                    <span className="option-label">{preset.label}</span>
                    <label className="toggle text-m">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={() => {
                          if (allSelected) {
                            // Turning OFF → deselect those values
                            onChange(
                              (selected as string[]).filter(
                                (v) => !preset.values.includes(v),
                              ),
                            );
                          } else {
                            // Turning ON → select ONLY the preset values
                            onChange(preset.values);
                          }
                        }}
                        disabled={!multiple}
                      />
                      <span className="slider round"></span>
                    </label>
                  </div>
                );
              })}
              {visibleOptions.map((opt) => (
                <div key={opt.value} className="multiselect-option text-m">
                  <span className="option-label">
                    {opt.icon}
                    {opt.label}
                  </span>
                  <label key={opt.value} className="toggle text-m">
                    <input
                      type="checkbox"
                      checked={isSelected(opt.value)}
                      onChange={() => toggleValue(opt.value)}
                      disabled={!multiple}
                    />
                    <span className="slider round"></span>
                  </label>
                </div>
              ))}
              {visibleOptions.length === 0 && (
                <div className="multiselect-empty text-s secondary-text">
                  Inga träffar
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
